import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { EliteFormFlowChartDataService } from '@features/elite-form/services/elite-form-flow-chart-data.service';
import { EliteFormManagerService } from '@features/elite-form/services/elite-form-manager.service';
import { EliteFormServiceFactory } from '@features/elite-form/services/elite-form-service-factory.service';
import { FormResponseViewService } from "@features/elite-form/services/form-response-view.service";
import { EliteFormValidationService } from '@features/elite-form/services/form-validation.service';
import { Store } from '@ngrx/store';
import { PlatformCommand } from '@shared/commands/command-base';
import { Api } from '@shared/constants/common-field.constants';
import { DialogDefinitionEditorBase, DialogField } from '@shared/components/platform-tool/platform-tool-dialog-definition/models/dialog-defnition-models';
import { MagicLinkModel } from '@shared/models/platform-tool-models';
import { getCurrentPlatformLink, ParticleState } from '@shared/store/reducers/particle.reduer';
import { takeUntil } from 'rxjs';
import { CommandService } from '@core/services/command.service';
import { DialogService } from '@shared/services/dialog-service';
import { FuseSplashScreenService } from '@barebone-fuse/@fuse/services/splash-screen.service';
import { DetailsInstructionService } from '@shared/services/details-instruction-service';
import {filter} from "rxjs/operators";
@Component({
    standalone: false,
    selector: 'dialog-filed-elite-form-group-transposed-editor',
    templateUrl: './dialog-filed-elite-form-group-transposed-editor.component.html',
    styleUrl: './dialog-filed-elite-form-group-transposed-editor.component.scss'
})
export class DialogFiledEliteFormGroupTransposedEditorComponent extends DialogDefinitionEditorBase implements OnInit, OnDestroy {
    eliteFormId: string = '';
    inputForm: FormGroup = new FormGroup({});
    childFlowchartId: string;
    firstTimeLoading = false;
    pages: any;
    platformDialogs = {};
    itemIds = [];
    eliteFormManagerService: EliteFormManagerService;
    private currentPlatformLink: MagicLinkModel;
    sectionForms: Map<string, FormGroup> = new Map<string, FormGroup>();
    autoSaveCommands: Map<string, PlatformCommand> = new Map<string, PlatformCommand>();
    private isInitialLoad = true;
    groupErrors: { [key: string]: number } = {};
    totalErrors: number = 0;


    constructor(
        private eliteFormFlowChartDataService: EliteFormFlowChartDataService,
        private eliteFormServiceFactory: EliteFormServiceFactory,
        private formResponseViewService: FormResponseViewService,
        private validationService: EliteFormValidationService,
        private particleStore: Store<ParticleState>,
        private commandService: CommandService,
        private dialogService: DialogService,
        private fuseSplashScreenService: FuseSplashScreenService,
        private detailsInstructionService: DetailsInstructionService
    ) { super() }

    ngOnInit(): void {
        super.ngOnInit();
        this.particleStore.select(getCurrentPlatformLink).pipe(takeUntil(this.unSubscribe$)).subscribe((platformLink: any) => {
            this.currentPlatformLink = platformLink;
        });

        this.eliteFormId = this.dialogField.fieldValues['EliteFormId'];
        this.childFlowchartId = this.dialogField.fieldValues['ChildFlowchartId'];
        this.eliteFormManagerService = this.eliteFormServiceFactory.getEliteFormManagerService(this.eliteFormId);

        this.validationService.getValidationState()
            .pipe(takeUntil(this.unSubscribe$))
            .subscribe(state => {
                this.groupErrors = state.groupErrors;
                this.totalErrors = state.totalErrors;
            });

        this.getPlatformDialogsByFlowchartId();
        this.isInitialLoad = true;

        this.eliteFormFlowChartDataService.responseInvalidateSubject
            .pipe(takeUntil(this.unSubscribe$), filter(eId => eId === this.eliteFormId))
            .subscribe(() => {
                this.getPlatformDialogsByFlowchartId();
            });
    }


    private getPlatformDialogsByFlowchartId() {
        this.dialogField.fieldValues = {
            ...this.dialogField.fieldValues,
            IsClientSchema: this.currentPlatformLink.fieldValues['IsClientSchema']
        };


        // Use the service method
        this.formResponseViewService.getPlatformDialogsByFlowchartId(
            this.eliteFormId,
            null,
            this.childFlowchartId,
            this.dialogField.fieldValues['SourceItemId'],
            this.dialogField.key,
            this.dialogField.fieldValues,
            this.currentPlatformLink.fieldValues['IsTranslated'],
            true // child group: render from PlatformDialogs only, skip the redundant pages query
        ).pipe(takeUntil(this.unSubscribe$)).subscribe(response => {
            this.pages = response.fieldValues['EliteFormPages'] || this.pages;
            this.platformDialogs = response.fieldValues['PlatformDialogs'];

            for (const itemId of Object.keys(this.platformDialogs)) {
                let asc = this.platformDialogs[itemId][0].bottomPanelActions.find(command => command.name === "UpdateEliteFormDataCommand");
                asc = this.formResponseViewService.initAutoSaveCommand(
                    asc,
                    this.eliteFormFlowChartDataService.responseId,
                    this.eliteFormFlowChartDataService.schemaId,
                    this.eliteFormFlowChartDataService.isClientSchema
                );

                this.autoSaveCommands.set(itemId, asc);
            }

            this.itemIds = Object.keys(this.platformDialogs);

            this.initializeSectionForms();

            setTimeout(() => {
                this.isInitialLoad = false;
            });
        });
    }

    private initializeSectionForms() {
        this.sectionForms.forEach((formGroup, itemId) => {
            this.validationService.unregisterFormGroup(itemId);
        });
        this.sectionForms.clear();

        for (const itemId of this.itemIds) {
            if (this.platformDialogs[itemId]?.[0]?.dialogDefinitions?.[0]?.fields) {
                const formGroup = new FormGroup({});
                const fields = this.platformDialogs[itemId][0].dialogDefinitions[0].fields;

                this.validationService.processFormValidation(
                    formGroup,
                    fields.filter(field => field.type !== 'Title' && field.type !== 'Description'),
                    {}
                );

                this.sectionForms.set(itemId, formGroup);
                this.validationService.registerFormGroup(itemId, formGroup, this.unSubscribe$);
            }
        }
    }

    ngOnDestroy() {
        this.sectionForms.forEach((formGroup, itemId) => {
            this.validationService.unregisterFormGroup(itemId);
        });
        super.ngOnDestroy();
    }


    getFieldFromSection(itemId: string, fieldKey: string): DialogField {
        if (!this.platformDialogs[itemId]?.[0]?.dialogDefinitions?.[0]?.fields) {
            return null;
        }

        const fields = this.platformDialogs[itemId][0].dialogDefinitions[0].fields;
        return fields.find(f => f.key === fieldKey);
    }

    getSectionForm(itemId: string): FormGroup {
        if (!this.sectionForms.has(itemId)) {
            const formGroup = new FormGroup({});
            if (this.platformDialogs[itemId]?.[0]?.dialogDefinitions?.[0]?.fields) {
                const fields = this.platformDialogs[itemId][0].dialogDefinitions[0].fields
                    .filter(field => field.type !== 'Title' && field.type !== 'Description');

                this.validationService.processFormValidation(formGroup, fields, {});
            }
            this.sectionForms.set(itemId, formGroup);
            this.validationService.registerFormGroup(itemId, formGroup, this.unSubscribe$);
        }

        return this.sectionForms.get(itemId);
    }

    onFieldValueChange(event: any, field: DialogField) {
        if (!field) return;

        for (const itemId of this.itemIds) {
            const fields = this.platformDialogs[itemId][0]?.dialogDefinitions[0]?.fields;
            if (fields) {
                const fieldIndex = fields.findIndex(f => f.key === field.key && f.fieldValues['ItemId'] === field.fieldValues['ItemId']);
                if (fieldIndex !== -1) {
                    fields[fieldIndex].value = event.value;

                    if (!this.isInitialLoad) {
                        this.formResponseViewService.updateFieldValue(
                            this.autoSaveCommands.get(itemId), field.key, event.value);

                        this.validationService.triggerValidation(field.key, itemId);
                    }
                }
            }
        }
    }

    onFieldValidationChange(event: any) { }
    onFieldDestroy(event: any) { }

    copySection() {
        const autoSaveCommand = this.dialogField.fieldValues['Commands'][0];
        autoSaveCommand.fieldValues['ResponseId'] = this.eliteFormFlowChartDataService.responseId;
        autoSaveCommand.fieldValues['SchemaId'] = this.eliteFormFlowChartDataService.schemaId;
        autoSaveCommand.fieldValues['IsClientSchema'] = this.eliteFormFlowChartDataService.isClientSchema;
        this.commandService.executePlatformCommand(autoSaveCommand)
            .pipe(takeUntil(this.unSubscribe$))
            .subscribe(() => {
                this.eliteFormFlowChartDataService.refreshResponseItems(this.eliteFormId);
                this.isInitialLoad = true;
            });
    }

    deleteSection(event: any, itemId: string) {
        event.stopPropagation();

        this.dialogService.showYesNoDialog("Delete Section", "Are you sure you want to delete this section?").pipe(takeUntil(this.unSubscribe$)).subscribe({
            next: (response) => {
                if (response && response.action === 'OK') {
                    this.fuseSplashScreenService.show();

                    const deleteCommand = this.platformDialogs[itemId][0].bottomPanelActions.find(
                        command => command.name === "DeleteEliteFormMultiResponseCommand"
                    );

                    if (deleteCommand) {
                        deleteCommand.fieldValues['ResponseId'] = this.eliteFormFlowChartDataService.responseId;
                        deleteCommand.fieldValues['SchemaId'] = this.eliteFormFlowChartDataService.schemaId;
                        deleteCommand.fieldValues['IsClientSchema'] = this.eliteFormFlowChartDataService.isClientSchema;

                        this.commandService.executePlatformCommand(deleteCommand)
                            .pipe(takeUntil(this.unSubscribe$))
                            .subscribe({
                                next: () => {
                                    this.eliteFormFlowChartDataService.refreshResponseItems(this.eliteFormId);
                                    this.isInitialLoad = true;
                                },
                                error: () => {
                                    this.fuseSplashScreenService.hide();
                                },
                                complete: () => {
                                    this.fuseSplashScreenService.hide();
                                }
                            });
                    } else {
                        this.fuseSplashScreenService.hide();
                    }
                }
            }
        });
    }

    onLockField(field: DialogField, lockStatus: boolean, itemId: string): void {
        const lockFieldCommand = new PlatformCommand('LockFormFieldCommand', Api.FilingManagerApi);
        lockFieldCommand.fieldValues['FieldKey'] = field.key;
        lockFieldCommand.fieldValues['IsLocked'] = lockStatus;
        lockFieldCommand.fieldValues['ItemId'] = itemId;
        this.commandService.executePlatformCommand(lockFieldCommand).pipe(takeUntil(this.unSubscribe$)).subscribe(res => {
            field.fieldValues['IsLocked'] = lockStatus;
            field.isDisabled = lockStatus;
            const sourceField = this.getFieldFromSection(itemId, field.key);
            if (sourceField) {
                sourceField.fieldValues['IsLocked'] = lockStatus;
                sourceField.isDisabled = lockStatus;
            }
            if(lockStatus){
                this.inputForm.controls[sourceField.key].disable();
            } else{
                this.inputForm.controls[sourceField.key].enable();
            }
        });
    }

    onRestoreField(field: DialogField, itemId: string): void {
        if(field == null || field.key == null) return;
        const defaultValue = field.fieldValues['DefaultValue'];
        const sectionForm = this.getSectionForm(itemId);
        if (sectionForm.get(field.key)) {
            sectionForm.get(field.key).setValue(defaultValue);
        }
    }


    trackById(index: number, field: DialogField): string {
        if(field.fieldValues['LockEnabled']){
        let suffix='';
        if(field.fieldValues['IsLocked']){
            suffix='-Locked'
        } else {
            suffix='-UnLocked';
        }
        return field.key+suffix;
        }
        return field.key;
    }

    openDetailsAndInstructions(field: any) {
        this.detailsInstructionService.setViewDetail(field.fieldValues['Details'],true);
    }
}
